import React, {
    FocusEvent,
    FocusEventHandler,
    MouseEventHandler,
    ReactNode,
    useCallback,
    useRef,
    useState,
} from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { varColor } from "../../utils/themeHelpers";
import { Icon, IconName } from "../common/Icon";
import { typographyCoreStyles, typographyStyles } from "../common/Typography";
import { BaseGroupButton, BaseGroupButtonMixins } from "./BaseGroupButton";
import { ButtonReset, splitButtonMixin } from "./Button.mixins";
import { renderIcon } from "./Group.styles";
import { byDisplayType } from "./IDisplayTypeProps";
import { DisplayType } from "./Picker.types";
import { useDisplayType } from "./PickerDisplayContext";
import { useSelectedFocusManager } from "./useSelectedFocusManager";

const Badge = styled.div<{ selected: boolean }>(
    typographyCoreStyles,
    typographyStyles.minimum,
    props => ({
        color: varColor("primary", "silver"),
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: props.selected
            ? varColor("primary", "silver")
            : varColor("primary", "steel03"),
        borderRadius: "1rem",
        fontWeight: 300,
        padding: "0px 8px 2px",
    }),
);

const LabelContent = styled.span`
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow-x: hidden;
`;

const Spacer = styled.span`
    flex-grow: 1;
`;

const InfoButton = styled("button", {
    shouldForwardProp: prop =>
        !["isSiblingFocused", "isSiblingOver"].includes(prop),
})<{
    isSiblingFocused: boolean;
    isSiblingOver: boolean;
    selected: boolean;
}>(
    ButtonReset,
    typographyCoreStyles,
    typographyStyles.list,
    splitButtonMixin,
    css`
        padding-inline-start: 0;
        &:last-of-type {
            border-top-right-radius: ${4 / 16}rem;
            border-bottom-right-radius: ${4 / 16}rem;
        }

        &:focus,
        &:hover {
            text-decoration: underline;
        }
    `,
    props => {
        if (props.isSiblingFocused) {
            return { color: varColor("primary", "white") };
        }
        if (props.isSiblingOver) {
            return { color: varColor("highlights", "blue") };
        }
        return { color: "transparent" };
    },
    BaseGroupButtonMixins.background,
);

const SplitButtonContainer = styled.div<{
    selected: boolean;
    displayType: DisplayType;
}>(
    {
        flexGrow: 1,
        marginInline: `${12 / 16}rem`,
        display: `flex`,
        borderRadius: `${4 / 16}rem`,
        maxWidth: `calc(100% - ${24 / 16}rem)`,
    },
    props =>
        props.selected
            ? null
            : css`
                  &:hover:not(:focus-within) > button {
                      background-color: ${byDisplayType({
                          popover: "var(--color-background-active)",
                          dropdown: "var(--color-background-secondary)",
                          small: "var(--color-background-secondary)",
                      })(props)};
                  }
              `,
    css`
        &:focus-within > button {
            background-color: var(--color-background-active2);
        }
    `,
);

const Check = styled(Icon)``;

interface IGroupButton {
    badge?: ReactNode;
    children: string;
    disabled?: boolean;
    hasInfo: boolean;
    icon?: IconName | null;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    onFocus?: FocusEventHandler<HTMLButtonElement>;
    selected: boolean;
    checked?: boolean;
}

export function GroupButton(props: IGroupButton) {
    const {
        badge,
        checked,
        disabled,
        hasInfo,
        icon,
        selected,
        onClick,
        onFocus,
        children,
    } = props;
    const [isFocused, setIsFocused] = useState(false);
    const [isOver, setIsOver] = useState(false);

    const handleFocus = useCallback(() => setIsFocused(true), []);
    const handleBlur = useCallback((e: FocusEvent<HTMLDivElement>) => {
        const siblingHasFocus =
            e.relatedTarget && e.currentTarget.contains(e.relatedTarget);
        if (siblingHasFocus) {
            return;
        }
        setIsFocused(false);
    }, []);

    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const handlePointerOver = useCallback(() => setIsOver(true), []);
    const handlePointerOut = useCallback(() => {
        setIsOver(false);
    }, []);

    useSelectedFocusManager(selected, containerRef, buttonRef);

    return (
        <SplitButtonContainer
            ref={containerRef}
            selected={selected}
            displayType={useDisplayType()}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onPointerOver={handlePointerOver}
            onPointerOut={handlePointerOut}
        >
            <BaseGroupButton
                ref={buttonRef}
                onClick={onClick}
                selected={selected}
                onFocus={onFocus}
                disabled={disabled}
            >
                {renderIcon(icon)}
                <LabelContent title={children}>{children}</LabelContent>
                {badge && <Badge selected={selected}>{badge}</Badge>}
                <Spacer />
                {checked && (
                    <Check name="check" color="currentColor" size="18" />
                )}
            </BaseGroupButton>
            {hasInfo && (
                <InfoButton
                    isSiblingFocused={isFocused}
                    isSiblingOver={isOver}
                    selected={selected}
                    disabled={disabled}
                >
                    Info{" "}
                    <Icon name="chevron-right" color="currentColor" size="18" />
                </InfoButton>
            )}
        </SplitButtonContainer>
    );
}
